// react
import { useCallback, useRef, useState } from 'react'

// external libs
import { Box, Button, makeStyles } from '@material-ui/core'
import { Form } from 'react-final-form'
import { FormApi } from 'final-form'

// components
import Dialog from '../../../control/dialog/dialog'
import Textfield from '../../../control/textfield/textfield'

// hooks
import useAuthenStore from '../../../stores/authen/authenStore'
import useChecklist from '../../../services/um/useChecklist'
import useLoading from '../../../control/loading/useLoading'
import useSnackbarAction from '../../../stores/snackbar/snackbarAction'
import useTranslate from '../../../i18n/useTranslate'

// types
import type { ChecklistRequest } from '../../../services/um/useChecklist'
import type { DialogType } from '../../../control/dialog/dialog'

export interface ChecklistFormProps {
    topicID: number
    onSubmitted?: () => void
}

function ChecklistForm(props: ChecklistFormProps) {
    const { topicID, onSubmitted } = props

    const translate = useTranslate()
    const classes = useStyles()
    const loadingHook = useLoading()
    const snackbarAction = useSnackbarAction()
    const checklistServices = useChecklist()
    const { profile } = useAuthenStore()
    const irb = Number(profile?.irb)

    const initialValues: ChecklistRequest = {
        nameTH: '',
        nameEN: '',
        topicID,
        irb,
        parentID: null,
        isAnswer: false,
        isDefault: false,
    }
    const dialogRef = useRef<Record<string, Function>>()
    const [dialogInfo, setDialogInfo] = useState<Partial<DialogType>>()

    async function createChecklist(values: ChecklistRequest, form: FormApi) {
        try {
            loadingHook.loadingOpen()

            const { status } = await checklistServices.createChecklist(values)

            if (status !== 1) {
                throw new Error(translate.get('MESSAGE.M022'))
            }

            snackbarAction.openSnackbar({
                message: translate.get('ALERT.CONTENT.SAVE_SUCCESS'),
                type: 'success',
            })
            form.reset()
            onSubmitted && onSubmitted()
        } catch (error: any) {
            setTimeout(
                () =>
                    snackbarAction.openSnackbar({
                        message: error.message,
                        type: 'error',
                    }),
                1000,
            )
        } finally {
            loadingHook.loadingClose()
        }
    }

    const onSubmit = useCallback(
        (values, form) => {
            const isValid = !!values.nameTH?.trim() && !!values.nameEN?.trim()

            if (isValid) {
                setDialogInfo({
                    title: `${translate.get('UM.SETTING_CONSIDERATION.MESSAGE.HEADER')}`,
                    content: `${translate.get('UM.SETTING_CONSIDERATION.MESSAGE.CONFIRM')}`,
                    type: 'alert-confirm',
                    onClickConfirm: () => {
                        dialogRef.current?.closeDialog()
                        createChecklist(values, form)
                    },
                })
            } else {
                setDialogInfo({
                    title: `${translate.get('UM.SETTING_CONSIDERATION.MESSAGE.HEADER')}`,
                    content: `${translate.get('MESSAGE.M023')}`,
                    type: 'alert-message',
                })
            }

            dialogRef.current?.openDialog()
        },
        [dialogRef, createChecklist],
    )

    return (
        <>
            <Form onSubmit={onSubmit} initialValues={initialValues}>
                {({ handleSubmit }) => {
                    return (
                        <form onSubmit={handleSubmit}>
                            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} style={{ gap: 16 }}>
                                <Box flexGrow={1} flexShrink={1} mb={{ xs: 2, md: 0 }}>
                                    <Textfield
                                        name="nameTH"
                                        materialInputProps={{
                                            label: `${translate.get('LABEL.MAIN_TOPIC')}(${translate.get(
                                                'LABEL.THAI',
                                            )})`,
                                            placeholder: `${translate.get('LABEL.MAIN_TOPIC')}(${translate.get(
                                                'LABEL.THAI',
                                            )})`,
                                        }}
                                    />
                                </Box>
                                <Box flexGrow={1} flexShrink={1} mb={{ xs: 2, md: 0 }}>
                                    <Textfield
                                        name="nameEN"
                                        materialInputProps={{
                                            label: `${translate.get('LABEL.MAIN_TOPIC')}(${translate.get(
                                                'LABEL.ENG',
                                            )})`,
                                            placeholder: `${translate.get('LABEL.MAIN_TOPIC')}(${translate.get(
                                                'LABEL.ENG',
                                            )})`,
                                        }}
                                    />
                                </Box>
                                <Button
                                    type="submit"
                                    className={classes.submitButton}
                                    size="large"
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                >
                                    {translate.get('UM.USERGROUP-ADD.TITLE')}
                                </Button>
                            </Box>
                        </form>
                    )
                }}
            </Form>

            <Dialog onRef={(funcs) => (dialogRef.current = funcs)} {...dialogInfo} />
        </>
    )
}

const useStyles = makeStyles((theme) => ({
    submitButton: {
        boxShadow: `0px 4px 4px ${theme.palette.grey['50']}`,
        height: '42px',
        flexShrink: 0,
        width: '100%',
        marginTop: 0,
        [theme.breakpoints.up('md')]: {
            width: 160,
            marginTop: 35,
        },
    },
}))

export default ChecklistForm
